// Secure email service for sending contact form emails
// This service provides secure API integration for Zoho email

export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  message: string;
}

export interface EmailResponse {
  success: boolean;
  message: string;
}

// Rate limiting to prevent spam
const rateLimitMap = new Map<string, { count: number; lastReset: number }>();
const RATE_LIMIT_WINDOW = 15 * 60 * 1000; // 15 minutes
const MAX_REQUESTS_PER_WINDOW = 3; // Max 3 requests per 15 minutes

const isRateLimited = (ip: string): boolean => {
  const now = Date.now();
  const userLimit = rateLimitMap.get(ip);
  
  if (!userLimit) {
    rateLimitMap.set(ip, { count: 1, lastReset: now });
    return false;
  }
  
  if (now - userLimit.lastReset > RATE_LIMIT_WINDOW) {
    rateLimitMap.set(ip, { count: 1, lastReset: now });
    return false;
  }
  
  if (userLimit.count >= MAX_REQUESTS_PER_WINDOW) {
    return true;
  }
  
  userLimit.count++;
  return false;
};

// Input sanitization
const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 1000); // Limit length
};

// Validate email format
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

// Validate phone format
const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[\d\s\-+()]+$/;
  return phoneRegex.test(phone) && phone.length >= 8 && phone.length <= 20;
};

// Main secure email sending function
export const sendContactEmail = async (formData: ContactFormData): Promise<EmailResponse> => {
  try {
    // Get client IP for rate limiting (in production, this should come from your backend)
    const clientIP = 'unknown'; // This should be passed from your backend
    
    // Rate limiting check
    if (isRateLimited(clientIP)) {
      return {
        success: false,
        message: 'Too many requests. Please try again later.'
      };
    }

    // Input validation and sanitization
    const sanitizedData: ContactFormData = {
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email).toLowerCase(),
      phone: sanitizeInput(formData.phone),
      address: sanitizeInput(formData.address),
      message: sanitizeInput(formData.message)
    };

    // Validate required fields
    if (!sanitizedData.name || sanitizedData.name.length < 2) {
      return {
        success: false,
        message: 'Name must be at least 2 characters long.'
      };
    }

    if (!isValidEmail(sanitizedData.email)) {
      return {
        success: false,
        message: 'Please provide a valid email address.'
      };
    }

    if (!isValidPhone(sanitizedData.phone)) {
      return {
        success: false,
        message: 'Please provide a valid phone number.'
      };
    }

    if (!sanitizedData.message || sanitizedData.message.length < 10) {
      return {
        success: false,
        message: 'Message must be at least 10 characters long.'
      };
    }

    // Send to secure backend API
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // CSRF protection
      },
      body: JSON.stringify(sanitizedData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'Failed to send email. Please try again later.'
      };
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('Email sending error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.'
    };
  }
};
